import * as React from "react";
import { Tooltip } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { checkedCircleIconSmall } from "../../../common/checkedCircleIcon";
import { triStateIcon } from "../../../common/triStateIcon";
import { timeSinceConvertor } from "./SiteAboutPageUtils/timeSinceConvertor";
import { useCameraLiveness } from "./cameraLivenessStore";
import {
  livenessNowMs,
  VIDEO_FRESH_MINUTES,
  videoFlowStatus,
} from "./SiteAboutPageUtils/videoFlowStatus";

// The camera row's connection line (ENG-2068). "Connected" (admin `connected`
// = able_to_connect_on_launch) only says the server answered at launch, so
// where camera_liveness/ has evidence the line says whether VIDEO is arriving.
// Without liveness (loading, failed, 404) it is exactly today's line.

const LINE_CLASS = "w-full font-mulish text-[12px] text-actuate-blue pt-[10px]";

const ago = (iso: string | null) =>
  iso ? timeSinceConvertor(Date.parse(iso) / 1000) : null;

const withTooltip = (title: string, content: React.ReactElement) => (
  <Tooltip title={title} placement="top-start">
    {content}
  </Tooltip>
);

export const VideoFlowStatusLine = ({
  siteId,
  cameraId,
  connected,
}: {
  siteId: number | null | undefined;
  cameraId: number;
  connected: boolean | null | undefined;
}) => {
  const liveness = useCameraLiveness(siteId ? Number(siteId) : null);
  const status = videoFlowStatus(
    liveness,
    cameraId,
    liveness ? livenessNowMs(liveness) : Date.now()
  );
  const lastVideo = ago(status.lastVideoAt);

  switch (status.state) {
    case "receiving":
      return withTooltip(
        lastVideo
          ? `Last video ${lastVideo}.`
          : "Video is arriving from this camera.",
        <div className={LINE_CLASS} data-testid="video-flow-status">
          {checkedCircleIconSmall}
          &nbsp;&nbsp;Receiving video
        </div>
      );
    case "not_receiving":
      return withTooltip(
        status.zeroBandwidth
          ? "The site is running, but this camera's stream is at 0 kbps. The server answering does not mean video is arriving."
          : `The site is running, but no video has arrived from this camera ${
              lastVideo ? `since ${lastVideo}` : "recently"
            } (expected within ${VIDEO_FRESH_MINUTES} minutes). The server answering does not mean video is arriving.`,
        <div className={LINE_CLASS} data-testid="video-flow-status">
          <WarningAmberRoundedIcon color="warning" fontSize="small" />
          &nbsp;&nbsp;Not receiving video
        </div>
      );
    case "not_checked":
      return withTooltip(
        "Video is only checked while the site's connector is running.",
        <div className={LINE_CLASS} data-testid="video-flow-status">
          <RemoveCircleOutline color="disabled" fontSize="small" />
          &nbsp;&nbsp;Not checked (site not armed)
        </div>
      );
    case "no_data":
      if (connected === true) {
        return withTooltip(
          "The camera's server answered when the connector launched. There is no live video reading for this camera (it may only send video on motion), so this is the launch check only.",
          <div className={LINE_CLASS} data-testid="video-flow-status">
            <InfoOutlinedIcon color="disabled" fontSize="small" />
            &nbsp;&nbsp;Server reachable at launch
          </div>
        );
      }
      break;
    default:
      break;
  }
  return (
    <div className={LINE_CLASS}>
      {triStateIcon(connected)}
      &nbsp;&nbsp;Connected
    </div>
  );
};
